import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useStore, type ChatMessage } from '../lib/store.ts';
import { apiFetch } from '../lib/api.ts';
import { refreshPlan } from './usePlan.ts';

const DEFAULT_LOADING_KEYS = [
  'chat.loading1',
  'chat.loading2',
  'chat.loading3',
] as const;

const DEFAULT_WARM_UP_KEY = 'chat.warmingUp';
const DEFAULT_ERROR_KEY = 'chat.errorSend';
const DEFAULT_ENDPOINT = '/chat/stream';

// If the model hasn't streamed a token in this many ms, switch the loading
// message to a "warming up" hint. Ollama on a cold model can take 8-15s on
// the first request, and a frozen-looking spinner is the worst UX.
const WARM_UP_MS = 5000;

interface ToolEvent {
  name: string;
  status: 'start' | 'done' | 'error';
  label?: string;
}

interface SsePayload {
  session_id?: string;
  conversation_id?: number;
  token?: string;
  tool?: ToolEvent;
  error?: string;
  done?: boolean;
  ai_sources?: string[];
}

interface StreamState {
  firstToken: boolean;
  // A lookup started after the coach already wrote something: start the
  // rest of the answer on a new paragraph instead of gluing it on.
  breakBeforeNext: boolean;
}

interface StreamHandlers {
  onSessionId: (id: string) => void;
  onConversationId: (id: number) => void;
  onFirstToken: (token: string) => void;
  onMoreToken: (token: string) => void;
  onToolStart: (tool: ToolEvent) => void;
  onToolEnd: () => void;
  onSources: (sources: string[]) => void;
  queryClient: QueryClient;
}

/** Agent chat: the coach is looking something up (status line only). */
function handleToolEvent(tool: ToolEvent, state: StreamState, handlers: StreamHandlers): void {
  if (tool.status === 'start') {
    handlers.onToolStart(tool);
    state.breakBeforeNext = !state.firstToken;
  } else {
    handlers.onToolEnd();
  }
}

/**
 * Process one SSE payload from the stream. Returns true when the stream
 * has signalled completion (`done: true`) so the caller can stop reading.
 * Throws if the payload contains an error.
 */
function handleSsePayload(
  payload: SsePayload,
  state: StreamState,
  handlers: StreamHandlers,
): boolean {
  if (payload.session_id) {
    handlers.onSessionId(payload.session_id);
    return false;
  }

  if (payload.error) {
    throw new Error(payload.error);
  }

  if (payload.tool) {
    handleToolEvent(payload.tool, state, handlers);
    return false;
  }

  if (payload.token) {
    if (state.firstToken) {
      handlers.onFirstToken(payload.token);
      state.firstToken = false;
    } else {
      handlers.onMoreToken(state.breakBeforeNext ? `\n\n${payload.token}` : payload.token);
    }
    state.breakBeforeNext = false;
  }

  if (payload.done) {
    if (payload.ai_sources?.length) {
      handlers.onSources(payload.ai_sources);
    }
    if (payload.conversation_id) {
      handlers.onConversationId(payload.conversation_id);
      handlers.queryClient.invalidateQueries({ queryKey: ['conversations'] });
    }
    return true;
  }

  return false;
}

/** Decode one ``data: {...}`` line; null for other lines or malformed JSON. */
function parseSseLine(line: string): SsePayload | null {
  if (!line.startsWith('data: ')) return null;
  try {
    return JSON.parse(line.slice(6)) as SsePayload;
  } catch {
    return null;
  }
}

/** Read & parse one chunk of the SSE stream into the buffer; returns updated buffer + parsed lines. */
function takeLines(buffer: string): { lines: string[]; rest: string } {
  const lines = buffer.split('\n');
  const rest = lines.pop() ?? '';
  return { lines, rest };
}

async function pumpStream(
  body: ReadableStream<Uint8Array>,
  handlers: StreamHandlers,
): Promise<StreamState> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  const state: StreamState = { firstToken: true, breakBeforeNext: false };

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const { lines, rest } = takeLines(buffer);
    buffer = rest;

    for (const line of lines) {
      const payload = parseSseLine(line);
      if (payload && handleSsePayload(payload, state, handlers)) return state;
    }
  }

  // A final event without a trailing newline is still an event.
  const last = parseSseLine(buffer + decoder.decode());
  if (last) handleSsePayload(last, state, handlers);

  return state;
}

/**
 * State injection contract for ``useChat``.
 *
 * Methods that return state are functions — not raw values — so that
 * async callbacks inside ``send`` always read the latest data. A
 * closure-captured plain value would freeze at the moment ``send`` was
 * created, leading to bugs like ``updateLastMessage`` writing against a
 * stale chat history.
 *
 * The default binding routes through the global Zustand store (cycling
 * chat behavior). Fuel chat (``pages/Fuel.tsx``) passes its own binding
 * tied to local React state so the cycling and nutrition conversations
 * stay independent.
 */
export interface ChatStateBinding {
  getHistory(): ChatMessage[];
  addMessage(msg: ChatMessage): void;
  updateLastMessage(content: string): void;
  getSessionId(): string | null;
  setSessionId(id: string): void;
  getConversationId(): number | null;
  setConversationId(id: number): void;
  /** Mark the last coach answer with the API's ``ai_sources``. */
  setLastSources?(sources: string[]): void;
}

export interface UseChatOptions {
  endpoint?: string;
  loadingKeys?: readonly string[];
  warmUpKey?: string;
  errorKey?: string;
  state?: ChatStateBinding;
}

function buildDefaultBinding(): ChatStateBinding {
  return {
    getHistory: () => useStore.getState().chatHistory,
    addMessage: (msg) => useStore.getState().addMessage(msg),
    updateLastMessage: (content) =>
      useStore.getState().updateLastMessage(content),
    getSessionId: () => useStore.getState().sessionId,
    setSessionId: (id) => useStore.getState().setSessionId(id),
    getConversationId: () => useStore.getState().conversationId,
    setConversationId: (id) => useStore.getState().setConversationId(id),
    setLastSources: (sources) => useStore.getState().setLastMessageSources(sources),
  };
}

export function useChat(options: UseChatOptions = {}) {
  const { t } = useTranslation();
  const endpoint = options.endpoint ?? DEFAULT_ENDPOINT;
  const loadingKeys = options.loadingKeys ?? DEFAULT_LOADING_KEYS;
  const warmUpKey = options.warmUpKey ?? DEFAULT_WARM_UP_KEY;
  const errorKey = options.errorKey ?? DEFAULT_ERROR_KEY;

  // ``bindingRef`` is initialized once on first render — the binding
  // object itself is meant to be stable. Live data flows through its
  // getter methods which reference React refs or ``useStore.getState()``.
  const bindingRef = useRef<ChatStateBinding | null>(null);
  bindingRef.current ??= options.state ?? buildDefaultBinding();
  const binding = bindingRef.current;

  const addToast = useStore((s) => s.addToast);
  const [isStreaming, setIsStreaming] = useState(false);
  const [loadingText, setLoadingText] = useState('');
  const abortRef = useRef<AbortController | null>(null);
  const queryClient = useQueryClient();

  const send = useCallback(
    async (message: string) => {
      binding.addMessage({ role: 'user', content: message });

      // Show a random loading message while waiting for first token
      const key = loadingKeys[Math.floor(Math.random() * loadingKeys.length)];
      setLoadingText(t(key));
      setIsStreaming(true);

      const controller = new AbortController();
      abortRef.current = controller;

      // Escalate the loading message if the model hasn't responded yet.
      const warmUpTimer = setTimeout(() => {
        setLoadingText(t(warmUpKey));
      }, WARM_UP_MS);

      const handlers: StreamHandlers = {
        onSessionId: binding.setSessionId,
        onConversationId: binding.setConversationId,
        onFirstToken: (token) => {
          clearTimeout(warmUpTimer);
          binding.addMessage({ role: 'coach', content: token });
          setLoadingText('');
        },
        onMoreToken: (token) => {
          setLoadingText('');
          const history = binding.getHistory();
          const lastMessage = history.at(-1);
          binding.updateLastMessage((lastMessage?.content ?? '') + token);
        },
        onToolStart: (tool) => {
          // A lookup is real progress, so drop the "warming up" escalation.
          clearTimeout(warmUpTimer);
          setLoadingText(t(`chat.tools.${tool.name}`, { defaultValue: tool.label ?? t('chat.loading2') }));
        },
        // After a lookup Claude often sends the whole answer at once, so say
        // it's being written rather than leaving a stale "Looking at…".
        onToolEnd: () => setLoadingText(t('chat.tools.writing')),
        onSources: (sources) => binding.setLastSources?.(sources),
        queryClient,
      };

      try {
        const body: Record<string, string | number> = { message };
        const sessionId = binding.getSessionId();
        const conversationId = binding.getConversationId();
        if (sessionId) body.session_id = sessionId;
        if (conversationId) body.conversation_id = conversationId;

        const resp = await apiFetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
          signal: controller.signal,
        });

        if (!resp.ok || !resp.body) {
          throw new Error(`HTTP ${resp.status}`);
        }

        const { firstToken } = await pumpStream(resp.body, handlers);

        // If we never got any tokens, add an empty coach message
        if (firstToken) {
          binding.addMessage({ role: 'coach', content: '' });
        }
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          addToast({ message: t(errorKey), type: 'error' });
        }
      } finally {
        clearTimeout(warmUpTimer);
        setIsStreaming(false);
        setLoadingText('');
        abortRef.current = null;
        // Free messages left / today's usage changed (or the gate answered).
        void refreshPlan();
      }
    },
    [binding, endpoint, loadingKeys, warmUpKey, errorKey, addToast, t, queryClient],
  );

  // Stop the in-flight reply, e.g. before switching conversation: otherwise
  // its final event re-selects the old conversation and the next message
  // lands in the wrong thread.
  const cancel = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  useEffect(() => cancel, [cancel]);

  return { send, cancel, isStreaming, loadingText };
}
